/**
 * Teams — who can see which templates, campaigns and groups.
 *
 * The tables, the five endpoints and team-scoped API keys all predate this
 * screen. What was missing was any way to *make* a team or put somebody in one,
 * so the team picker on the API Keys screen was permanently empty and read as
 * broken rather than unused — exactly where Groups was before it got a screen.
 *
 * A team is not a permission. Roles decide what somebody may do; a team decides
 * what they can see. The two are easy to confuse and the screen says so, because
 * the confusion has a specific painful shape: a marketer who belongs to no team
 * cannot see a template assigned to one, which looks like a bug in the templates
 * list rather than a visibility rule several screens away.
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import type { CursorPage, Team } from "../api/types";

const PAGE_SIZE = 50;

export function Teams() {
  const { orgId, can } = useSession();
  const navigate = useNavigate();
  const manageable = can("teams:manage");

  const [history, setHistory] = useState<string[]>([]);
  const cursor = history.at(-1) ?? "";

  const { data, error, loading, reload } = useAsync<CursorPage<Team>>(
    (signal) =>
      api.get<CursorPage<Team>>("/v1/teams", { signal, query: { limit: PAGE_SIZE, cursor } }),
    [orgId, cursor],
  );

  const rows = data?.data ?? [];

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Teams</h1>
        <div className="row">
          {manageable && <CreateTeam onCreated={reload} />}
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>
      </div>

      <div className="content">
        <HowTeamsWork />

        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table teams-table" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>Name</span>
              <span className="oc-table__cell--right">Created</span>
              <span className="oc-table__cell--right">Action</span>
            </div>

            <div className="oc-table__body">
              {error && (
                <p className="field-error" style={{ padding: "var(--oc-gutter)" }}>
                  {error.message}
                </p>
              )}

              {loading && rows.length === 0 && <SkeletonRows columns={3} />}

              {!error && rows.length === 0 && !loading && (
                <div className="oc-empty">
                  <div className="oc-empty__title">No teams yet</div>
                  <div className="oc-empty__hint">
                    Without teams everyone in the organization sees every template, campaign
                    and group, which is usually what a small deployment wants. Make one when
                    two groups of people need to work on separate sending without each
                    other's drafts in the way.
                  </div>
                </div>
              )}

              {rows.map((team) => (
                <TeamRow
                  key={team.id}
                  team={team}
                  removable={manageable}
                  onRemoved={reload}
                  onOpen={() => navigate(`/teams/${team.id}`)}
                />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">
                {rows.length === 0 ? "No teams" : `${rows.length} shown`}
              </span>
              <span style={{ display: "flex", gap: "var(--oc-space-2)" }}>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={history.length === 0}
                  onClick={() => setHistory((h) => h.slice(0, -1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={!data?.has_more}
                  onClick={() => data?.next_cursor && setHistory((h) => [...h, data.next_cursor!])}
                >
                  Next
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * The rule, stated once, where somebody is about to act on it.
 *
 * Teams are the one concept in the product whose effect is invisible from the
 * screen it affects: a template assigned to a team simply does not appear for
 * somebody outside it, with nothing to say why. Documentation elsewhere does not
 * reach the person staring at a list that seems to be missing a row.
 */
function HowTeamsWork() {
  const { role } = useSession();
  const orgWide = role === "owner" || role === "admin";

  return (
    <div className="oc-panel" style={{ padding: "var(--oc-space-4)" }}>
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        A team decides what people can <strong>see</strong>, not what they may do — roles do
        that. Templates, campaigns, groups and API keys can each belong to a team, and
        anything assigned to one is visible only to its members. Anything left unassigned
        stays visible to everybody.
      </p>
      <p className="oc-body-sm oc-text-secondary" style={{ marginBottom: 0 }}>
        {orgWide ? (
          <>
            Owners and admins see every team's work regardless of membership, so this list is
            complete for you — but a developer or marketer outside a team will not see what is
            assigned to it.
          </>
        ) : (
          <>
            You are not an owner or admin, so you see the teams you belong to plus everything
            left unassigned. Work assigned to a team you are not in is hidden from you
            entirely, which is why a template somebody mentions may not appear in your list.
          </>
        )}
      </p>
    </div>
  );
}

function TeamRow({
  team,
  removable,
  onRemoved,
  onOpen,
}: {
  team: Team;
  removable: boolean;
  onRemoved: () => void;
  onOpen: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    // Named consequences rather than "are you sure": what a team deletion does
    // to the work assigned to it is the thing somebody needs to weigh, and it
    // is not obvious.
    if (
      !window.confirm(
        `Delete the team "${team.name}"?\n\n` +
          `Its templates, campaigns and groups are not deleted — they become ` +
          `unassigned, which makes them visible to everyone in the organization.`,
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/teams/${team.id}`);
      onRemoved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
      setBusy(false);
    }
  };

  return (
    // The whole row is the target, matching Groups. A link inside a row that is
    // also clickable gives two hit areas for one destination and a keyboard
    // order that stops twice on the same thing.
    <div
      className="oc-table__row"
      role="row"
      tabIndex={0}
      style={{ cursor: "pointer" }}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="oc-body-sm oc-truncate">
        {team.name}
        {error && (
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
      </span>
      <span className="oc-table__cell--right oc-mono-sm oc-text-muted-aa">
        {new Date(team.created_at).toLocaleDateString()}
      </span>
      <span className="oc-table__cell--right">
        {removable && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--xs"
            disabled={busy}
            // Stops the row's own handler: a click on Delete must not also
            // navigate into the team being deleted.
            onClick={(event) => {
              event.stopPropagation();
              void remove();
            }}
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        )}
      </span>
    </div>
  );
}

function CreateTeam({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post<Team>("/v1/teams", { name: name.trim() });
      setName("");
      setOpen(false);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="oc-btn oc-btn--primary oc-btn--sm" onClick={() => setOpen(true)}>
        New team
      </button>
    );
  }

  return (
    <form className="row" onSubmit={(event) => void submit(event)}>
      <input
        className="oc-input oc-input--sm"
        autoFocus
        required
        maxLength={200}
        placeholder="Growth"
        value={name}
        onChange={(event) => setName(event.target.value)}
        aria-label="Team name"
        aria-invalid={error ? true : undefined}
      />
      <button
        type="submit"
        className="oc-btn oc-btn--primary oc-btn--sm"
        disabled={busy || name.trim() === ""}
      >
        {busy ? "Creating…" : "Create"}
      </button>
      <button
        type="button"
        className="oc-btn oc-btn--ghost oc-btn--sm"
        onClick={() => {
          setOpen(false);
          setError(null);
        }}
      >
        Cancel
      </button>
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </form>
  );
}
